<template>
  <div class="wc-row">
    <div class="wc-col wc-col__sm-12 wc-col__md-12">
      <div v-if="vorgang !== ''">
        <VehicleChangeCompleted
          v-if="vorgang == 'fahrzeug-wechseln'"
          :conclusion-number="conclusionNumber"
          :evb-number="evbNumber"
        />
        <TeaserAccountTile
          v-else
          :footerDivider="false"
          :primaryButtonColor="ButtonColor.LINK"
          :primaryButtonLink="url"
          :primaryButtonText="buttonText"
          :showImage="
            vorgang ==
            (isDa()
              ? 'digitale-schadenkarte-anfordern'
              : 'digitale-versicherungskarte-anfordern')
          "
          :text="text"
          :title="title"
          ><template v-slot:image>
            <TeaserWallet
              v-if="
                vorgang ==
                (isDa()
                  ? 'digitale-schadenkarte-anfordern'
                  : 'digitale-versicherungskarte-anfordern')
              "
            /> </template
        ></TeaserAccountTile>
      </div>
      <Modal
        :autoOpened="true"
        :centeredText="false"
        :headerDivider="true"
        :max-width="700"
        :show-modal="showRatingModal && isTrustpilotEnabled"
        :showCloseIcon="true"
        :title="$t('thankYouPage.ratingModalTitle')"
        @closeModal="closeModal"
      >
        <template #content>
          <p>
            {{ $t('thankYouPage.trustpilotFeedbackText') }}
          </p>
          <img
            :src="trustpilotLogoPath"
            alt=""
            style="width: 50%; min-width: 150px; max-width: 150px"
          />
          <p>{{ $t('thankYouPage.yourOpinionMattersText') }}</p>
        </template>
        <template #footer>
          <div class="footer-cta footer-cta--left">
            <CustomButton :buttonColor="ButtonColor.PRIMARY" @click="openRatingPage">
              {{ $t('thankYouPage.ratingButtonText') }}
            </CustomButton>
            <CustomButton :buttonColor="ButtonColor.LINK" @click="closeModal">
              {{ $t('thankYouPage.cancelButtonText') }}
            </CustomButton>
          </div>
        </template>
      </Modal>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import TeaserAccountTile from '@/components/rahmen/TeaserAccountTile.vue'
import { ButtonColor, CustomButton, Modal } from 'de-zurich-ui-library'
import { useRoute } from 'vue-router'
import { t } from '@/i18n'
import TeaserWallet from '@/componentsTenant/rahmen/images/TeaserWallet.vue'
import { ratingLinksApi } from '@/stores/partnerApiClient'
import { isDa, isTrue } from '@/utils/CommonUtils'
import VehicleChangeCompleted from '@/componentsTenant/motor/vehiclechange/VehicleChangeCompleted.vue'
import { usePoliciesStore } from '@/stores/policiesStore'

const policiesStore = usePoliciesStore()
const route = useRoute()

const buttonText = ref()
const url = ref()
const vorgang = ref('')

const conclusionNumber = ref('')
const evbNumber = ref()
const isMulti = ref()
const isOldtimer = ref()
const licensePlate = ref()
const mail = ref()
const pageQuery = Object.keys(route.query)[0]
const text = ref()
const title = ref()

const assetsBaseUrl = ref(globalThis.ENV_CONFIG?.ASSETS_BASE_URL.CUSTOMER_PORTAL || '')
const fallbackTrustpilotLink = 'https://de.trustpilot.com/evaluate/da-direkt.de'
const ratingLink = ref('')
const showRatingModal = ref(false)

// Maps vorgang URL params to feature flag keys
const VORGANG_TO_FLAG_MAP: Record<string, string> = {
  'adresse-aendern': 'ADDRESS_CHANGE',
  'bankdaten-aendern': 'BANK_DATA_CHANGE',
  'bescheinigung-fuer-begleitetes-fahren-anfordern': 'ACCOMPANIED_DRIVING_CERTIFICATE',
  'digitale-schadenkarte-anfordern': 'DIGITAL_CARD_REQUEST',
  'fahrzeug-wechseln': 'VEHICLE_CHANGE',
  'internationale-versicherungskarte-anfordern': 'INTERNATIONAL_INSURANCE_CARD',
  'name-aendern': 'NAME_CHANGE',
  'vertrag-aendern': 'CONTRACT_CHANGE'
}

const trustpilotLogoPath = computed(() =>
  isTrustpilotEnabled.value
    ? `${assetsBaseUrl.value}/assets/images/Trustpilot_Logo.png`
    : ''
)

const isTrustpilotEnabled = computed(() => {
  const trustpilotFlags = (
    globalThis.ENV_CONFIG?.FEATURE_FLAGS.PARTNER_PROXY as
      { TRUSTPILOT?: Record<string, boolean | undefined> } | undefined
  )?.TRUSTPILOT

  const flagKey = VORGANG_TO_FLAG_MAP[vorgang.value]
  return isTrue(flagKey ? (trustpilotFlags?.[flagKey] ?? false) : false)
})
function getTrustpilotTag() {
  const tagKey = `thankYouPage.${vorgang.value}.tag`
  const tag = t(tagKey).trim()

  return tag && tag !== tagKey ? tag : ''
}

function setTextAndTitleForRequestDigitalCard() {
  setTextAndTitleForCardRequest()
}

function setTextAndTitleForRequestIVK() {
  setTextAndTitleForCardRequest()
}

function setTextAndTitleForCardRequest() {
  let textPath: string, titlePath: string

  if (isOldtimer.value) {
    textPath = 'thankYouPage.ivk-oldtimer.text'
    titlePath = 'thankYouPage.ivk-oldtimer.title'
  } else if (licensePlate.value?.includes(',')) {
    textPath = `thankYouPage.${vorgang.value}2.text`
    titlePath = `thankYouPage.${vorgang.value}2.title`
  } else if (isMulti.value) {
    textPath = `thankYouPage.${vorgang.value}.textMulti`
    titlePath = `thankYouPage.${vorgang.value}.titleMulti`
  } else {
    textPath = `thankYouPage.${vorgang.value}.text`
    titlePath = `thankYouPage.${vorgang.value}.title`
  }
  title.value = t(titlePath)
  text.value = t(textPath, {
    mail: mail.value,
    licensePlate: licensePlate.value
  })
}

function handleVehicleChange() {
  const values = policiesStore.consumeVehicleChangeThankYouValues()
  conclusionNumber.value = values?.conclusionNumber ?? ''
  evbNumber.value = values?.evbNumber ?? ''
}

function handleAccompaniedDrivingRequest() {
  licensePlate.value =
    policiesStore.consumeAccompaniedDrivingThankYouValues()?.licensePlate ?? ''
  url.value = globalThis.ENV_CONFIG?.SERVICE_LINKS.VERTRAGSSERVICES_PAGE.ROOT
  buttonText.value = t(`thankYouPage.buttonTextServices`)
  setTextAndTitleForRequestDigitalCard()
}

function handleDigitalCardRequest() {
  const values = policiesStore.consumeDigitalCardThankYouValues()
  mail.value = values?.mail ?? ''
  isMulti.value = values?.isMulti ?? ''
  url.value = globalThis.ENV_CONFIG?.REDIRECTS.ACCOUNT_LOGIN_ON_SUCCESS_REDIRECT
  buttonText.value = t(`thankYouPage.buttonTextDigitalCard`)
  setTextAndTitleForRequestDigitalCard()
}

function handleIVKRequest() {
  const values = policiesStore.consumeIvkThankYouValues()
  isOldtimer.value = values?.isOldtimer ?? false
  mail.value = values?.mail ?? ''
  licensePlate.value = values?.licensePlate ?? ''
  url.value = globalThis.ENV_CONFIG?.SERVICE_LINKS.VERTRAGSSERVICES_PAGE.ROOT
  buttonText.value = t(`thankYouPage.buttonTextServices`)
  setTextAndTitleForRequestIVK()
}

function configureButtonForProcess(process: string) {
  if (
    [
      'beitragsbescheinigung-anfordern',
      'bescheinigung-fuer-begleitetes-fahren-anfordern',
      'internationale-versicherungskarte-anfordern',
      'vertrag-aendern'
    ].includes(process)
  ) {
    url.value = globalThis.ENV_CONFIG?.SERVICE_LINKS.VERTRAGSSERVICES_PAGE.ROOT
    buttonText.value = t(`thankYouPage.buttonTextServices`)
  } else if (process !== 'fahrzeug-wechseln') {
    url.value = globalThis.ENV_CONFIG?.REDIRECTS.PERSONAL_PROFIL_PAGE
    buttonText.value = t(`thankYouPage.primaryButtonText`)
  }
}

function handleStandardProcess() {
  title.value = t(`thankYouPage.${vorgang.value}.title`)
  text.value = t(`thankYouPage.${vorgang.value}.text`)

  if (pageQuery === 'fahrzeug-wechseln') {
    handleVehicleChange()
  } else {
    configureButtonForProcess(pageQuery!)
  }
}

function initializeTrustpilotModal() {
  if (isTrustpilotEnabled.value) {
    setTimeout(() => {
      const tnpsModal = document.querySelector('#kampyleFormContainer')
      if (!tnpsModal || getComputedStyle(tnpsModal).visibility === 'hidden') {
        fetchRatingLink()
      }
    }, 5000)
  }
}

onMounted(() => {
  vorgang.value = pageQuery || ''

  if (
    pageQuery ===
    (isDa() ? 'digitale-schadenkarte-anfordern' : 'digitale-versicherungskarte-anfordern')
  ) {
    handleDigitalCardRequest()
  } else if (vorgang.value === 'internationale-versicherungskarte-anfordern') {
    handleIVKRequest()
  } else if (vorgang.value === 'bescheinigung-fuer-begleitetes-fahren-anfordern') {
    handleAccompaniedDrivingRequest()
  } else {
    handleStandardProcess()
  }

  initializeTrustpilotModal()

  policiesStore.resetThankYouValues()
})

async function fetchRatingLink() {
  if (!isTrustpilotEnabled.value) {
    return
  }

  const tag = getTrustpilotTag()
  if (!tag) {
    return
  }

  showRatingModal.value = true
  ratingLink.value = fallbackTrustpilotLink

  try {
    const response = await ratingLinksApi().generateLink({
      generateTrustpilotLinkRequest: {
        tags: [tag],
        firstName: sessionStorage.getItem('firstName') ?? '',
        lastName: sessionStorage.getItem('lastName') ?? ''
      }
    })
    ratingLink.value = response.link
  } catch {
    ratingLink.value = fallbackTrustpilotLink
  }
}

function openRatingPage() {
  globalThis.open(ratingLink.value, '_blank')
}

function closeModal() {
  showRatingModal.value = false
}
</script>
